/**
 * Signup (plan 032): Firebase Auth owns the password store. Flow:
 *
 *   1. Call Firebase `createUserWithEmailAndPassword` — fails cleanly
 *      if the email is already in use, before we touch anything local.
 *   2. Generate the 32-byte family key + wrap it under
 *      `PBKDF2(password + salt)` (unchanged plan-018 crypto), stashed
 *      in IndexedDB via `storeFamilyKey(email, password, familyKey)`.
 *   3. Exchange the Firebase ID token at `/v1/auth/firebase/callback`
 *      to bootstrap the Backstop session; the CP creates the parent +
 *      family + enqueues an inline email-verification code.
 *   4. Route to the mnemonic-reveal screen.
 *
 * Google signup path is unchanged — `signInWithGoogle` → same callback
 * → `storeFamilyKeyFirebase` for the wrap (no password to derive from).
 */

import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Input } from '../components/Input';
import {
  firebaseConfigured,
  friendlyFirebaseError,
  signInWithGoogle,
  signUpWithPassword,
} from '../data/firebase';
import { completeFirebaseCallback } from '../data/firebase-callback';
import { setFlash } from '../state/flash';
import { captureIntentFromUrl } from '../state/checkout-intent';
import { storeFamilyKey, wipeLocalVault } from '../state/session';

const MIN_PASSWORD = 10;

export function SignupRoute() {
  const location = useLocation();
  const [email, setEmail] = useState(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('email') ?? '';
  });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);

  useEffect(() => {
    captureIntentFromUrl();
  }, []);

  function routeNext(
    next: 'reveal-mnemonic' | 'dashboard' | 'restore-mnemonic',
  ) {
    switch (next) {
      case 'reveal-mnemonic':
        location.route('/onboarding/mnemonic');
        break;
      case 'dashboard':
        location.route('/');
        break;
      case 'restore-mnemonic':
        location.route('/login/restore-mnemonic');
        break;
    }
  }

  async function onGoogle() {
    setErr(null);
    setGoogleBusy(true);
    try {
      const idToken = await signInWithGoogle();
      const result = await completeFirebaseCallback({
        email, // filled below from the popup profile? Firebase user object
        // has .email; we pass what the form has, empty is fine — the
        // callback route stores the CP-side email based on the token
        // claim, and setAuthSession only uses this for the local
        // vault namespace which is normalised in session.ts.
        idToken,
        provider: 'google.com',
      });
      if (result.linked) {
        setFlash({
          kind: 'linked-google',
          message:
            'Linked your Google account to your existing Backstop login.',
        });
      }
      routeNext(result.next.kind);
    } catch (e) {
      const msg = friendlyFirebaseError(e);
      if (msg !== '') setErr(msg);
    } finally {
      setGoogleBusy(false);
    }
  }

  async function onSubmit(e: Event) {
    e.preventDefault();
    setErr(null);

    if (password.length < MIN_PASSWORD) {
      setErr(
        `Password must be at least ${MIN_PASSWORD.toString()} characters.`,
      );
      return;
    }
    if (password !== confirm) {
      setErr('Passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      // Firebase signup FIRST — cheapest to fail (auth/email-already-
      // in-use fires here without touching our local vault). On
      // success we get an ID token to feed the CP callback.
      let idToken: string;
      try {
        idToken = await signUpWithPassword(email, password);
      } catch (fbErr) {
        setErr(friendlyFirebaseError(fbErr));
        return;
      }

      // Wrap the family key under the password (same as pre-032).
      const familyKey = crypto.getRandomValues(new Uint8Array(32));
      await storeFamilyKey(email, password, familyKey);

      try {
        const result = await completeFirebaseCallback({
          email,
          idToken,
          provider: 'password',
        });
        routeNext(result.next.kind);
      } catch (cbErr) {
        // CP failure after Firebase already accepted the signup:
        // Firebase-side account exists but our side didn't create a
        // parent. Roll back the local vault so a retry doesn't
        // inherit the wrapped key under a different (server-unknown)
        // parent. The Firebase user itself is left orphaned; if the
        // parent retries with the same email + password, Firebase
        // returns an existing user's ID token and the CP callback
        // creates the parent then.
        await wipeLocalVault(email);
        throw cbErr;
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main class="flex min-h-screen items-center justify-center p-4">
      <Card class="w-full max-w-md space-y-4">
        <h1 class="text-2xl font-semibold">Create your Backstop account</h1>
        <p class="text-sm text-surface-fg/70">
          Your family&apos;s data is end-to-end encrypted. The next screen shows
          a 24-word recovery phrase that is your only way back in if you forget
          your password or switch devices.
        </p>
        {firebaseConfigured() && (
          <>
            <Button
              type="button"
              onClick={() => void onGoogle()}
              disabled={googleBusy || busy}
              variant="secondary"
              class="w-full"
            >
              {googleBusy ? 'Opening Google…' : 'Continue with Google'}
            </Button>
            <div
              class="relative my-1 text-center text-xs uppercase tracking-wide text-surface-fg/50"
              aria-hidden="true"
            >
              <span class="relative z-10 bg-surface-card px-2">or</span>
              <span class="absolute left-0 right-0 top-1/2 -z-0 h-px bg-surface-fg/20" />
            </div>
          </>
        )}
        <form onSubmit={onSubmit} class="space-y-3" noValidate>
          <label class="block space-y-1 text-sm">
            <span>Email</span>
            <Input
              type="email"
              value={email}
              onInput={(e: Event) => {
                setEmail((e.target as HTMLInputElement).value);
              }}
              required
              autocomplete="email"
            />
          </label>
          <label class="block space-y-1 text-sm">
            <span>Password</span>
            <Input
              type="password"
              value={password}
              onInput={(e: Event) => {
                setPassword((e.target as HTMLInputElement).value);
              }}
              required
              minLength={MIN_PASSWORD}
              autocomplete="new-password"
            />
          </label>
          <label class="block space-y-1 text-sm">
            <span>Confirm password</span>
            <Input
              type="password"
              value={confirm}
              onInput={(e: Event) => {
                setConfirm((e.target as HTMLInputElement).value);
              }}
              required
              autocomplete="new-password"
            />
          </label>
          {err !== null && (
            <p class="text-sm text-danger-500" role="alert">
              {err}
            </p>
          )}
          <Button type="submit" disabled={busy}>
            {busy ? 'Creating…' : 'Create account'}
          </Button>
        </form>
      </Card>
    </main>
  );
}
